import { HasCreatedTimestamp } from '@ts/shared/created-edited/util-core';
import { ShippingMethodCode } from '@ts/shipping/shared/util-core';
import { User } from '@ts/user/shared/util-core';

export type OrderStatus =
  | 'Ready for payment'
  | 'Awaiting payment'
  | 'Payment failure'
  | 'Paid'
  | 'Ready for shipping'
  | 'Awaiting shipping'
  | 'Shipping failure'
  | 'In shipping'
  | 'Cancelled'
  | 'Complete';

export const ORDER_STATUSES: OrderStatus[] = [
  'Ready for payment',
  'Awaiting payment',
  'Payment failure',
  'Paid',
  'Ready for shipping',
  'Awaiting shipping',
  'Shipping failure',
  'In shipping',
  'Cancelled',
  'Complete',
];

/**
 * The `order` scope key naming a status. Awaiting shipping reads by the
 * shipping method (#11398): "Menunggu Kurir" for a courier, "Siap Diambil"
 * for a pickup. Without the method, e.g. a status filter, it names both.
 */
export function getOrderStatusTranslationKey(
  orderStatus: OrderStatus,
  shippingMethodCode: ShippingMethodCode | null,
): string {
  switch (orderStatus) {
    case 'Ready for payment':
      return 'order.status.readyForPayment';
    case 'Awaiting payment':
      return 'order.status.awaitingPayment';
    case 'Payment failure':
      return 'order.status.paymentFailure';
    case 'Paid':
      return 'order.status.paid';
    case 'Ready for shipping':
      return 'order.status.readyForShipping';
    case 'Awaiting shipping':
      return getAwaitingShippingTranslationKey(shippingMethodCode);
    case 'Shipping failure':
      return 'order.status.shippingFailure';
    case 'In shipping':
      return 'order.status.inShipping';
    case 'Cancelled':
      return 'order.status.cancelled';
    case 'Complete':
      return 'order.status.complete';
  }
}

function getAwaitingShippingTranslationKey(
  shippingMethodCode: ShippingMethodCode | null,
): string {
  switch (shippingMethodCode) {
    case 'biteship:grab:instant':
    case 'biteship:grab:instant_car':
    case 'biteship:lalamove:mpv':
    case 'biteship:lalamove:truck':
    case 'biteship:lalamove:van':
      return 'order.status.awaitingShippingCourier';
    case 'self-pickup':
      return 'order.status.awaitingShippingPickup';
    case 'point-of-sales':
    case null:
      return 'order.status.awaitingShipping';
  }
}

// `success` for a good outcome, `danger` for a failed or cancelled one.
export type OrderStatusTone = 'info' | 'success' | 'danger';

export interface OrderStatusMetaBase {
  // An ionicon by the status's meaning: payment, shipping, done, cancelled.
  iconName: string;
  tone: OrderStatusTone;
}

export type OrderStatusMeta = Readonly<OrderStatusMetaBase>;

// How a status is marked wherever it shows, so it never reads by colour
// alone (#11398 D2).
export function getOrderStatusMeta(orderStatus: OrderStatus): OrderStatusMeta {
  switch (orderStatus) {
    case 'Ready for payment':
    case 'Awaiting payment':
      return { iconName: 'card', tone: 'info' };
    case 'Paid':
      return { iconName: 'card', tone: 'success' };
    case 'Payment failure':
      return { iconName: 'card', tone: 'danger' };
    case 'Ready for shipping':
    case 'Awaiting shipping':
      return { iconName: 'cube', tone: 'info' };
    case 'In shipping':
      return { iconName: 'car', tone: 'info' };
    case 'Shipping failure':
      return { iconName: 'car', tone: 'danger' };
    case 'Cancelled':
      return { iconName: 'close', tone: 'danger' };
    case 'Complete':
      return { iconName: 'checkmark', tone: 'success' };
  }
}

// The courier is booked or on the way. Every tracking fetch calls Biteship
// (apishipping/views.py), so outside these statuses it is fetched on demand.
export function isOrderShippingTrackingLive(orderStatus: OrderStatus): boolean {
  return orderStatus === 'Awaiting shipping' || orderStatus === 'In shipping';
}

/** A status staff may move an order to (`status_valid_get`, #12044). */
export interface OrderStatusMoveStaffBase {
  status: OrderStatus;
  // A move the courier's webhook usually makes; the backend asks for a reason.
  is_staff_override: boolean;
}

export type OrderStatusMoveStaff = Readonly<OrderStatusMoveStaffBase>;

// Why a status changed (#11663); mirrors `OrderStatusChangeReasonStaff` and
// `OrderStatusChangeReasonSystem` in py/mono/solvent/order/order_status.py.
// Staff pick from the first; the system writes the second.
export const ORDER_STATUS_CHANGE_REASONS_STAFF = [
  'item_out_of_stock',
  'item_quality',
  'payment_failed',
  'customer_request',
  'order_changed',
  'courier_not_found',
  'other',
] as const;

export type OrderStatusChangeReasonStaff =
  (typeof ORDER_STATUS_CHANGE_REASONS_STAFF)[number];

export const ORDER_STATUS_CHANGE_REASONS_SYSTEM = [
  'payment_deadline_exceeded',
  'cancelled_by_customer',
  'payment_cancelled_by_customer',
  'requesting_payment',
  'payment_retried_by_customer',
  'offline_sales_auto_completion',
] as const;

export type OrderStatusChangeReasonSystem =
  (typeof ORDER_STATUS_CHANGE_REASONS_SYSTEM)[number];

export type OrderStatusChangeReason =
  | OrderStatusChangeReasonStaff
  | OrderStatusChangeReasonSystem;

/** A status change as the customer reads it: why, never who or a note. */
export interface OrderStatusEventBase extends HasCreatedTimestamp {
  status_before: OrderStatus;
  status_after: OrderStatus;
  // Null for a plain move along the pipeline.
  reason: OrderStatusChangeReason | null;
}

export type OrderStatusEvent = Readonly<OrderStatusEventBase>;

/** A status change as staff read it: who made it, and any note. */
export interface OrderStatusEventStaffBase extends OrderStatusEventBase {
  notes: string;
  // Null for a change the system made.
  user: User | null;
}

export type OrderStatusEventStaff = Readonly<OrderStatusEventStaffBase>;

/**
 * A staff status change (`status_set`). The backend decides when a reason is
 * needed; `note` is always sent, blank without a reason and required for
 * `other`.
 */
export interface OrderStatusChangeParams {
  status_from: OrderStatus;
  // The new status.
  status: OrderStatus;
  reason: OrderStatusChangeReasonStaff | null;
  note: string;
}
